/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SHOPS YOU FOLLOW — ALL CARDS page (section-level "See More" from Home)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Same shape as searchHistoryAllCards.js / viewingActivityAllCards.js.
 *
 * Each card keeps its own existing behavior untouched: its "See More" still
 * opens /lakay-ShopSeller/:owner (the existing shop page), and its
 * follow/unfollow icon still works (loadProductsByArrival wired the same
 * way as the Home followShopUser section, via reloadFacets).
 *
 * Includes Search History + Viewing Activity as companion sliders below the
 * grid, each linking to its own all-cards page.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Layout from '../Layout/Layout';
import useDataLoader from '../pageInside/exeemple_page/DataLoader';
import DynamicPageRenderer from '../pageInside/exeemple_page/DynamicPageRenderer';
import { getInfoSourceUpdates } from '../pageInside/exeemple_page/allFacets';
import { PAGES_CONFIG_, SECTIONS_LIBRARY } from '../pageInside/exeemple_page/pagesConfig';
import { isAuthenticated } from '../auth';
import { prices } from '../pageInside/fixedPrices';
import { getPaginationLimit } from '../../utils/paginationSettings';

const PAGE_ID = 'shopsYouFollowAllCards';
const SECTION_ID = 'followShopUserGrid';

const DEFAULT_FACETS = (() => {
    const config = PAGES_CONFIG_[PAGE_ID];
    if (!config?.sections?.length) {
        return [SECTION_ID, 'historySearchMatrix', 'userViewMatrix'];
    }
    return config.sections
        .filter(s => s.enabled !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => s.sectionId);
})();

const _extractFromSettings = (settings) => {
    if (!settings || typeof settings !== 'object') return null;
    const { sections, facets } = settings;

    if (Array.isArray(sections) && sections.length > 0) {
        const sorted = [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const resolvedFacets = sorted
            .filter(s => s.sectionId && s.enabled !== false)
            .map(s => s.sectionId);
        if (resolvedFacets.length > 0) return { facets: resolvedFacets };
    }
    if (Array.isArray(facets) && facets.length > 0) return { facets };
    return null;
};

const resolvePageConfig = (pageSettings) =>
    _extractFromSettings(pageSettings) || { facets: DEFAULT_FACETS };

const ShopsYouFollowAllCards = (props) => {
    const { infoSource, laguangeElakay, homeStyles, nbrArr } = props;
    const { token } = isAuthenticated();

    const [myFilters, setMyFilters] = useState({ filters: {} });
    const handleFilters = useCallback((filters, filterBy) => {
        setMyFilters(prev => ({ ...prev, filters: { ...prev.filters, [filterBy]: filters } }));
    }, []);

    const pageSettings = infoSource?.allSettings?.pages?.[PAGE_ID];
    const { facets } = useMemo(() => resolvePageConfig(pageSettings), [pageSettings]);

    // Admin-configurable via AnimationSettings → General → "Pagination &
    // Limites" (paginationLimits.shopsYouFollowAllCards) — same key
    // MatrixCardsGrid already reads for the grid's own "Load More" page
    // size; reused here for this page's combined companion-sliders fetch
    // (historySearchMatrix / userViewMatrix), same pattern as home.jsx/
    // userViewed.js. Falls back to 12 (the previous hardcoded value).
    const effectiveLimit = getPaginationLimit(infoSource?.allSettings, PAGE_ID, 12);

    const {
        pageData,
        loading,
        mergePageData,
        loadPageData,
        reloadFacets,
    } = useDataLoader({
        facets,
        infoSource,
        token,
        skip: 0,
        limit: effectiveLimit,
        filters: myFilters.filters,
    });

    useEffect(() => {
        const updates = getInfoSourceUpdates(facets, infoSource);
        if (Object.keys(updates).length > 0) mergePageData(updates);
    }, [infoSource, facets]);

    const facetsKey = useMemo(() => facets.join(','), [facets]);
    useEffect(() => {
        loadPageData();
    }, [facetsKey]);

    const pageTitle = SECTIONS_LIBRARY[SECTION_ID]?.meta?.title || 'Shops You Follow';

    return (
        <Layout
            infoSource={infoSource}
            loading={loading}
            prices={prices}
            handleFilters={handleFilters}
            setMyFilters={setMyFilters}
            SliderPunShow={false}
            P1={true}
            {...infoSource?.allSettings?.layout}
        >
            <div style={infoSource.styleNav}>
                <div className="container-fluid pt-3">
                    <h1 className="h3 mb-2 px-2">{pageTitle}</h1>
                </div>

                <DynamicPageRenderer
                    facets={facets}
                    pageId={PAGE_ID}
                    pageData={pageData}
                    infoSource={infoSource}
                    laguangeElakay={laguangeElakay}
                    homeStyles={homeStyles}
                    page={PAGE_ID}
                    myFilters={myFilters}
                    nbrArr={nbrArr}
                    reloadFacets={reloadFacets}
                />

                {loading && (
                    <div className="row justify-content-center mt-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">
                                {infoSource.laguangeElakay('frontElakayHardcodedText', infoSource.languageAPI)?.userViewed?.t001 || 'Loading...'}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ShopsYouFollowAllCards;
